import ReactBilling from '@web-solutions/react-billing';
import { isFoodzeeBackend } from '@web-solutions/react-billing/constants';
import Attribution from '@web-solutions/module-attribution';

import API, { NMI_API, setBillingBaseUrlOverride } from './api';

import { getExtraParams } from './utils';

const STRIPE_PAYMENT_SYSTEM = 'stripe';
const TAILOREDPAY_PAYMENT_SYSTEM = 'tailoredpay';

/**
 * FB ad labels (campaign/adset/creative) captured on the landing, forwarded so
 * the cohort report can attribute revenue to a campaign. `{}` for an organic
 * visit — the object is omitted from the body rather than sent empty.
 *
 * Idempotent by contract: the backend remembers the first snapshot it ever gets
 * for a user and ignores every later one, and never fails a payment because of
 * it — so sending it on every request is safe and expected.
 */
const getAttributionParams = () => {
  const attribution = Attribution.getAdAttribution();

  return attribution ? { attribution } : {};
};

/**
 * Same, for the `/payments/*` endpoints. Those are shared with Solidgate and
 * Recurly, whose billing is a third-party API — the labels only go out when the
 * request actually reaches the Foodzee backend, i.e. for Stripe and TailoredPay
 * (see FOODZEE_BACKEND_SYSTEMS). The `/nmi/*` endpoints need no such check:
 * they only ever run for TailoredPay.
 */
const getFoodzeeAttributionParams = (paymentSystemType) =>
  isFoodzeeBackend(paymentSystemType) ? getAttributionParams() : {};

const unwrapData = (response) => {
  if (response && Object.prototype.hasOwnProperty.call(response, 'data')) {
    return response.data;
  }

  return response;
};

const getFoodzeeApiBaseUrl = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const prodUrl = process.env.REACT_APP_FOODZEE_PROD_URL;
  const isProd = prodUrl && currentUrl.startsWith(prodUrl) && !currentUrl.includes('stage');

  return isProd
    ? process.env.REACT_APP_API_FOODZE_BASE_URL_PROD
    : process.env.REACT_APP_API_FOODZE_BASE_URL_STAGE;
};

const Billing = {
  paymentSystemProject: '',
  paymentSystemType: '',

  setApiAuth(apiAuth) {
    this.apiAuth = apiAuth;
  },

  getPaymentParams({ paymentSystem, paymentProject } = {}) {
    return {
      payment_system_type: paymentSystem || this.paymentSystemType,
      payment_system_project: paymentProject || this.paymentSystemProject,
    };
  },

  getHeaders() {
    return {
      Authorization: this.apiAuth,
    };
  },

  async init(paymentSystem, paymentProject) {
    this.paymentSystemType = paymentSystem;
    this.paymentSystemProject = paymentProject;

    if (isFoodzeeBackend(paymentSystem)) {
      const foodzeeBillingUrl = getFoodzeeApiBaseUrl();
      console.log('[Billing] setting Foodzee billing URL override:', foodzeeBillingUrl);
      setBillingBaseUrlOverride(foodzeeBillingUrl);
    } else {
      setBillingBaseUrlOverride(null);
    }

    const headers = this.getHeaders();

    // TailoredPay bootstraps from /nmi/config rather than /payments/config, and
    // the response is the gateway config itself (script URLs + public keys +
    // feature flags), not a map of provider keys.
    if (paymentSystem === TAILOREDPAY_PAYMENT_SYSTEM) {
      const config = unwrapData(
        await NMI_API.getConfig(
          { payment_system_project: this.paymentSystemProject },
          headers,
        ),
      );

      this.tailoredpayConfig = config;

      // Gateway not rolled out for this project. Return WITHOUT entering
      // ReactBilling.init: that call would set the module-level payment system
      // to 'tailoredpay' before rejecting on the disabled flag, and the card
      // form reads that global — so the form would keep rendering as TailoredPay
      // even after the store has fallen back to the reserve system, and fail at
      // submit with "Collect.js is not loaded".
      if (!config || config.enabled === false) {
        console.log('[Billing] TailoredPay is disabled for this project — falling back to reserve');
        return config;
      }

      return ReactBilling.init(paymentSystem, { tailoredpay: config })
        .then(() => config);
    }

    try {
      const keys = await API.getPublicKey(this.getPaymentParams(), headers);
      return ReactBilling.init(paymentSystem, keys);
    } catch (e) {
      console.error('[Billing] init: getPublicKey FAILED', e);
      throw e;
    }
  },

  getTailoredpayConfig() {
    return this.tailoredpayConfig;
  },

  /** Number of plans the last /nmi/plans call returned; undefined if not called. */
  getTailoredpayPlansCount() {
    return this.tailoredpayPlansCount;
  },

  async getPrices(pricesIds, { paymentSystem } = {}) {
    const headers = this.getHeaders();

    // TailoredPay plans come from /nmi/plans. Same filter contract as
    // /payments/products — ask only for the plans this funnel actually shows.
    // The ids here are already the `tailoredpayId` values: prepareProductsIds()
    // mapped them onto `product.id` before we got here.
    if ((paymentSystem || this.paymentSystemType) === TAILOREDPAY_PAYMENT_SYSTEM) {
      const ids = [...new Set(pricesIds)].filter(Boolean);

      // `search` only — /nmi/plans takes no `searchFields`, unlike
      // /payments/products. With no ids configured yet (tailoredpayId not filled
      // in remote config) the filter is omitted entirely rather than sent empty,
      // so the gateway returns everything and a config mismatch stays visible
      // instead of looking like "there are no plans".
      const params = {
        payment_system_project: this.paymentSystemProject,
        ...(ids.length ? { search: `external_id:${ids.join(',')}` } : {}),
      };

      const plans = unwrapData(await NMI_API.getPlans(params, headers)) || [];

      // Remembered so the store can tell "the gateway has nothing to sell"
      // (a real reason to fall back) apart from "the gateway has plans but our
      // remote config doesn't reference them" (a config bug worth surfacing).
      this.tailoredpayPlansCount = plans.length;

      return plans;
    }

    const params = {
      search: `external_id:${pricesIds.length !== 0 ? pricesIds.join(','): '40_foodzee_5_io3_month,20_foodzee_2_io3_month'}`,
      searchFields: 'external_id:in',
      ...this.getPaymentParams({ paymentSystem }),
    };
    return unwrapData(await API.getPrices(params, headers)) || [];
  },

  async createOrder({
    one_time_product,
    one_time_products,
    productId,
    orderDescription,
    products,
    currency,
    paymentSystem,
    trigger,
    metadata,
    solidMetadata,
    is_sending_terminate_links,
    is_sending_trial_end
  }) {
    const extra = await getExtraParams();

    const body = {
      ...this.getPaymentParams({ paymentSystem }),
      ...extra,
      one_time_product,
      trigger,
      metadata: JSON.stringify(metadata),
      solid_metadata: solidMetadata,
      one_time_products: one_time_products || undefined,
      product_id: productId,
      order_description: orderDescription,
      products,
      currency,
      is_sending_terminate_links,
      is_sending_trial_end
    };

    return API.createOrder(body, this.getHeaders());
  },

  async initAlternativePayment({
    paymentMethod,
    paymentSystem,
    trigger,
    products,
    currency,
    metadata,
    one_time_products,
    orderDescription,
    is_sending_terminate_links,
    is_sending_trial_end,
    solid_metadata,
    first_name,
    last_name,
    taxpayer_id,
    success_url,
    fail_url,
    return_url,
  }) {
    const extra = await getExtraParams();

    const body = {
      ...this.getPaymentParams({ paymentSystem }),
      ...extra,
      products,
      solid_metadata,
      currency,
      metadata: JSON.stringify(metadata),
      one_time_products,
      trigger,
      order_description: orderDescription,
      is_sending_terminate_links,
      is_sending_trial_end,
      payment_method: paymentMethod,
      customer_first_name: first_name,
      customer_last_name: last_name,
      taxpayer_id,
      success_url,
      fail_url,
      return_url,
    };

    return API.initAlternativePayment(body, this.getHeaders())
  },

  async createCheckout({ productId, checkoutId, paymentMethod, successUrl, paymentSystem }) {
    const extra = await getExtraParams();
    const body = {
      ...this.getPaymentParams({ paymentSystem }),
      ...extra,
      product_id: productId,
      checkout_id: checkoutId,
      payment_method: paymentMethod,
      success_url: successUrl,
    };

    return API.createCheckout(body, this.getHeaders());
  },

  /**
   * TailoredPay first payment. Charges `trialAmount` now and starts `planId` in
   * `trialDays` days — a single request, unlike the Stripe flow which needs a
   * separate one-time charge plus a trialing subscription.
   *
   * The subscription amount is NOT sent: it comes from the plan in the gateway.
   */
  async nmiCheckout({
    planId,
    trialAmount,
    trialDays,
    paymentToken,
    clientRequestId,
    currency,
    productCode,
    email,
    threeDS,
    billingAddress,
    paymentMethodKind,
  }) {
    const extra = await getExtraParams();

    const body = {
      payment_system_project: this.paymentSystemProject,
      plan_id: planId,
      trial_amount: trialAmount,
      trial_days: trialDays,
      payment_token: paymentToken,
      client_request_id: clientRequestId,
      currency,
      product_code: productCode,
      email,
      three_ds: threeDS,
      idfm: extra.idfm,
      device_id: extra.idfm,
      ...getAttributionParams(),
      // Only sent when something was actually collected, so a funnel that asks
      // for neither a ZIP nor an Apple Pay address keeps the body it had before.
      ...(billingAddress ? { billing_address: billingAddress } : {}),
      // 'apple_pay' / 'google_pay'; absent for a plain card.
      ...(paymentMethodKind ? { payment_method_kind: paymentMethodKind } : {}),
    };

    return unwrapData(await NMI_API.checkout(body, this.getHeaders()));
  },

  async nmiCheckoutStatus(subscriptionRef) {
    return unwrapData(await NMI_API.checkoutStatus(subscriptionRef, this.getHeaders()));
  },

  /**
   * Upsells and one-off products. Unlike the checkout, the amount is passed from
   * the front end. `paymentToken` may be omitted for an already subscribed user —
   * the saved card is charged, so no card form and no 3DS are needed.
   */
  async nmiOneTime({
    amount,
    currency,
    productCode,
    clientRequestId,
    paymentToken,
    email,
    threeDS,
    trigger,
    paymentMethodKind,
  }) {
    const body = {
      payment_system_project: this.paymentSystemProject,
      amount,
      currency,
      product_code: productCode,
      client_request_id: clientRequestId,
      payment_token: paymentToken,
      email,
      three_ds: threeDS,
      trigger,
      ...getAttributionParams(),
      // Wallet kind, and only when a fresh token is being sent — a charge against
      // the card on file is not a wallet transaction.
      ...(paymentMethodKind && paymentToken
        ? { payment_method_kind: paymentMethodKind }
        : {}),
    };

    return unwrapData(await NMI_API.oneTime(body, this.getHeaders()));
  },

  async nmiGetSubscription() {
    return unwrapData(await NMI_API.getSubscription(this.getHeaders()));
  },

  async nmiGetPayments() {
    return unwrapData(await NMI_API.getPayments(this.getHeaders()));
  },

  async nmiCancelSubscription(params = {}) {
    return unwrapData(await NMI_API.cancelSubscription(params, this.getHeaders()));
  },

  async nmiResumeSubscription(params = {}) {
    return unwrapData(await NMI_API.resumeSubscription(params, this.getHeaders()));
  },

  async createCustomer({ email, paymentSystem, paymentProject }) {
    const headers = this.getHeaders();
    const paymentParams = this.getPaymentParams({ paymentSystem, paymentProject });
    const extra = paymentParams.payment_system_type === STRIPE_PAYMENT_SYSTEM
      ? await getExtraParams()
      : {};
    const body = {
      ...paymentParams,
      ...getFoodzeeAttributionParams(paymentParams.payment_system_type),
      ...(extra.idfm ? { idfm: extra.idfm } : {}),
      ...(email && { email }),
    };

    return unwrapData(await API.createCustomer(body, headers));
  },

  async subscribe({ paymentSystem, metadata, ...formData }) {
    const headers = this.getHeaders();
    const extra = await getExtraParams();
    const paymentParams = this.getPaymentParams({ paymentSystem });

    const body = {
      ...extra,
      ...formData,
      ...paymentParams,
      ...getFoodzeeAttributionParams(paymentParams.payment_system_type),
      metadata: JSON.stringify(metadata),
    };

    return unwrapData(await API.createSubscription(body, headers));
  },

  async confirmSubscriptionPayment(subscriptionId) {
    return unwrapData(await API.confirmSubscriptionPayment(subscriptionId, this.getHeaders()));
  },

  async subscribeOnOneClick({ productId, metadata, ...params }) {
    const body = {
      ...this.getPaymentParams(),
      ...params,
      metadata: JSON.stringify(metadata),
      product_id: productId,
    };

    const response = await API.subscribeOnOneClick(body, this.getHeaders())

    return response
  },

  async applePayOrder({ paymentSystem, metadata, ...formData }) {
    const headers = this.getHeaders();
    const extra = await getExtraParams();

    const body = {
      ...extra,
      ...formData,
      customer_email: formData.email,
      product_id: formData.price_id,
      metadata: JSON.stringify(metadata),
      ...this.getPaymentParams({ paymentSystem }),
    };

    return API.createApplePayOrder(body, headers);
  },

  async getSubscriptions(isAsb) {
    const headers = this.getHeaders();
    if (isAsb) {
      return API.getAllSubscriptions(headers);
    }
    return API.getActualizedSubscriptions(headers);
  },

  async requestCancel({ email, trigger }) {
    const body = {
      email,
      trigger,
    };

    return API.cancelSubscription(body);
  },

  async submitCancelSubscription(params) {
    return API.submitCancelSubscription(params);
  },

  async createOneClickPayment({
    amount,
    currency,
    force3ds,
    productCode,
    productId,
    paymentMethodId,
    setupFutureUsage,
    transactionId,
    description,
    successUrl,
    failUrl,
    trigger,
    metadata,
    solidMetadata,
    idfm,
    withTransactionDetailsEmail
  }) {
    const headers = this.getHeaders();
    const params = this.getPaymentParams();
    const isStripePayment = params.payment_system_type === STRIPE_PAYMENT_SYSTEM;
    const metadataProductId = isStripePayment
      ? (productCode || productId)
      : (productId || productCode);
    const metadataIdfm = idfm || description;
    const paymentMetadata = isStripePayment
      ? {
        ...(metadata || {}),
        ...(metadataProductId ? { product_id: metadataProductId } : {}),
        ...(metadataIdfm ? { idfm: metadataIdfm } : {}),
      }
      : metadata;

    const body = {
      ...this.getPaymentParams(),
      ...getFoodzeeAttributionParams(params.payment_system_type),
      withTransactionDetailsEmail,
      force3ds,
      amount,
      description,
      currency,
      trigger,
      ...(isStripePayment && metadataIdfm ? { idfm: metadataIdfm } : {}),
      metadata: JSON.stringify(paymentMetadata),
      solid_metadata: solidMetadata,
      ...(paymentMethodId ? { payment_method_id: paymentMethodId } : {}),
      ...(setupFutureUsage ? { setup_future_usage: setupFutureUsage } : {}),
      success_url: successUrl,
      fail_url: failUrl,
      product_code: productCode,
      ...(isStripePayment && {
        product_id: metadataProductId,
      }),
    };

    return API.createOneClickPayment(body, headers);
  },

  async getPaymentMethod() {
    const headers = this.getHeaders();
    const paymentMethod = await API.getPaymentMethod(headers);
    return paymentMethod?.data || paymentMethod;
  },

  async getOneTimePurchases() {
    const headers = this.getHeaders();
    const oneTimePurchases = await API.getOneTimePurchases(headers);
    return oneTimePurchases?.data || oneTimePurchases;
  },

  async sendAccessEmail(email) {
    const headers = this.getHeaders();
    const body = {
      email
    }

    return API.sendAccessEmail(body, headers);
  },

  async changePlan({ subscriptionId, productId, isNewSubscription, successUrl, failUrl }) {
    const headers = this.getHeaders();

    const body = {
      ...this.getPaymentParams(),
      subscriptionId,
      planCode: productId,
      isNewSubscription: !!isNewSubscription,
      success_url: successUrl,
      fail_url: failUrl,
    };

    return API.changePlan(body, headers);
  },

  async giveProduct(productId) {
    const headers = this.getHeaders();

    const body = {
      ...this.getPaymentParams(),
      product_code: productId,
    };

    return API.giveProduct(body, headers);
  }
};

export default Billing;
